/**
 * One job's working state.
 *
 * Holds the shot statuses, the verdicts, the answers the estimator has given to
 * prompts, and whether the estimate has been issued — then derives everything
 * else from the tested modules rather than storing it twice.
 *
 * Deriving rather than storing is the point: there is exactly one path from a
 * verdict to a total, so the number in the job bar cannot drift from the number
 * on the estimate.
 */

import { useCallback, useMemo, useState } from 'react';
import { applyGate, acceptanceRate, type Candidate, type Verdict } from '../ai/review.js';
import { buildEstimate, type CatalogEntry, type ExclusionRule } from '../estimate/build.js';
import { calculate } from '../calc/engine.js';
import { captureGaps, shotListFor, type ImpactPoint, type ShotState } from '../capture/shotlist.js';
import { revokePhoto, type CapturedPhoto } from '../capture/photos.js';
import type { EstimateLine, RateProfile } from '../calc/types.js';
import type { JobDraft } from '../data/repo.js';

export interface UseJobInput {
  impact: ImpactPoint;
  initialShots: ShotState[];
  candidates: Candidate[];
  /**
   * Verdicts already recorded on this job.
   *
   * These come from the database. Starting empty instead — which this did —
   * meant every decision was written correctly and then thrown away on the
   * next load: reopen the job and it asked you to judge the same eleven
   * proposals again, with the acceptance rate reset to nothing. The decisions
   * were never lost, only unread.
   */
  initialVerdicts?: Record<string, Verdict>;
  /**
   * Edits already saved on this job: prices, hours, dismissed prompts, hand
   * lines. Absent on a job nobody has priced yet.
   */
  initialDraft?: JobDraft;
  /** The frozen version, if the job already has one. */
  initialIssued?: { at: string; version: number; hash: string; total: number } | null;
  /** How the customer already answered, if they have. */
  initialAnswer?: 'approved' | 'partly' | 'declined' | null;
  catalog: CatalogEntry[];
  exclusions: ExclusionRule[];
  profile: RateProfile;
  acv?: number;
  /**
   * Called when the estimator overrules the model on repair-versus-replace.
   *
   * It hangs off useJob rather than off each screen on purpose. Review and
   * Price BOTH offer the control, and when persistence lived in one screen's
   * prop the other screen quietly dropped the label. One path, no way past it.
   */
  onOperationOverride?: (key: string, operation: string) => void;
  /** This vehicle's body style and make, so catalog rows are picked for it. */
  bodyStyle?: 'sedan' | 'pickup' | 'suv' | 'van' | 'other';
  make?: string;
}

const keyOf = (p: { zone: string; partLabel: string }) => `${p.zone}::${p.partLabel}`;

export function useJob(input: UseJobInput) {
  const [shots, setShots] = useState<ShotState[]>(input.initialShots);
  const [photos, setPhotos] = useState<Record<string, CapturedPhoto>>({});
  const [verdicts, setVerdicts] = useState<Record<string, Verdict>>(
    input.initialVerdicts ?? {});
  // All five seeded from the saved draft. Starting empty — which they did —
  // meant an estimator who priced eight lines and was called away lost every
  // one of them, with nothing on screen to suggest anything was at risk.
  const d = input.initialDraft;
  const [refinishHours, setRefinishHours] =
    useState<Record<string, number>>(d?.refinishHours ?? {});
  const [manualHours, setManualHours] =
    useState<Record<string, number>>(d?.manualHours ?? {});
  // Quantity is an estimator decision too — two clips, four bolts — and it was
  // fixed at whatever the proposal implied with no way to change it.
  const [quantities, setQuantities] =
    useState<Record<string, number>>(d?.quantities ?? {});
  const [prices, setPrices] = useState<Record<string, number>>(d?.prices ?? {});
  const [dismissed, setDismissed] = useState<string[]>(d?.dismissed ?? []);
  // Struck-off lines and hand edits. Both are draft state, never destructive:
  // the proposal underneath stays as the record of what the model said.
  const [operations, setOperations] =
    useState<Record<string, string>>(d?.operations ?? {});
  const [removedLines, setRemovedLines] = useState<string[]>(d?.removedLines ?? []);
  const [lineEdits, setLineEdits] = useState<NonNullable<JobDraft['lineEdits']>>(
    d?.lineEdits ?? {});
  const [extra, setExtra] = useState<EstimateLine[]>(
    (d?.extra as EstimateLine[] | undefined) ?? []);
  // Both seeded from the database. Starting at null meant a reload forgot the
  // estimate had been issued AND forgot the customer had answered: the app
  // offered to issue it a second time, and the approval poll never started
  // because nothing believed there was anything to wait for.
  const [issued, setIssued] = useState<
    { at: string; total: number; version: number; hash: string } | null>(
      input.initialIssued ?? null);
  const [answer, setAnswer] = useState<'approved' | 'partly' | 'declined' | null>(
    input.initialAnswer ?? null);

  const list = useMemo(() => shotListFor(input.impact), [input.impact]);
  const gaps = useMemo(() => captureGaps(list, shots), [list, shots]);

  const proposals = useMemo(
    () => applyGate({ candidates: input.candidates, uncoveredZones: gaps.uncoveredZones })
      // The estimator's call, applied AFTER the gate — the gate is about what
      // was photographed and how sure the model is, neither of which changes
      // because a human decided to repair rather than replace.
      .map((p) => {
        const chosen = operations[`${p.zone}::${p.partLabel}`];
        return chosen && chosen !== p.operation
          ? { ...p, operation: chosen as typeof p.operation, proposedOperation: p.operation }
          : p;
      }),
    [input.candidates, gaps.uncoveredZones, operations],
  );

  /** Prices the estimator has typed in count as catalog entries from then on. */
  const catalog = useMemo<CatalogEntry[]>(() => [
    ...input.catalog,
    ...Object.entries(prices).map(([key, price]) => ({
      description: key.split('::')[1] ?? key,
      partType: 'new_oem' as const,
      price,
    })),
  ], [input.catalog, prices]);

  const built = useMemo(
    () => buildEstimate({
      proposals, verdicts, catalog,
      exclusions: input.exclusions,
      refinishHours,
      dismissedPrompts: dismissed,
      bodyStyle: input.bodyStyle,
      make: input.make,
    }),
    [proposals, verdicts, catalog, input.exclusions, refinishHours, dismissed,
     input.bodyStyle, input.make],
  );

  /** Hours the estimator has entered, applied on top of the built lines. */
  const lines = useMemo<EstimateLine[]>(() => [
    ...built.lines.filter((l) => !removedLines.includes(l.id)).map((l) => {
      const edit = lineEdits[l.id];
      if (edit) {
        l = {
          ...l,
          ...(edit.description ? { description: edit.description } : {}),
          ...(edit.partNumber !== undefined ? { partNumber: edit.partNumber } : {}),
          ...(edit.partType ? { partType: edit.partType as typeof l.partType } : {}),
        };
      }
      const hours = manualHours[l.id];
      const qty = quantities[l.id];
      // A price typed on this job overrides the catalog for this job only, so
      // the badge has to follow the override rather than the catalog.
      const priceKey = l.id.includes('#') ? l.id.slice(0, l.id.lastIndexOf('#')) : l.id;
      const typedPrice = prices[priceKey] !== undefined;
      if (hours === undefined && qty === undefined && !typedPrice) return l;
      return {
        ...l,
        ...(hours !== undefined ? { hours, hoursSource: 'typed' as const } : {}),
        ...(qty !== undefined ? { quantity: qty } : {}),
        ...(typedPrice && l.partPrice > 0 ? { priceSource: 'typed' as const } : {}),
      };
    }),
    ...extra,
  ], [built.lines, manualHours, quantities, prices, extra, removedLines, lineEdits]);

  const totals = useMemo(
    () => calculate({
      lines, profile: input.profile,
      vehicle: input.acv ? { acv: input.acv, thresholdPercent: input.profile.taxes.length ? 80 : 80 } : undefined,
    }),
    [lines, input.profile, input.acv],
  );

  const rate = useMemo(() => acceptanceRate(proposals, verdicts, keyOf), [proposals, verdicts]);

  const capture = useCallback((id: string, photo?: CapturedPhoto) => {
    setShots((prev) => (prev.some((s) => s.id === id)
      ? prev.map((s) => (s.id === id ? { id, status: 'taken' as const } : s))
      : [...prev, { id, status: 'taken' as const }]));

    if (!photo) return;
    setPhotos((prev) => {
      // Retaking a shot replaces it. The URL it held is released here, because
      // nothing else will ever hold a reference to it again.
      revokePhoto(prev[id]);
      return { ...prev, [id]: photo };
    });
  }, []);

  const decide = useCallback((key: string, v: Verdict) => {
    // Changing a verdict after issuing would change a frozen document.
    if (issued) return;
    setVerdicts((prev) => ({ ...prev, [key]: v }));
  }, [issued]);

  /**
   * Freeze the estimate.
   *
   * The version and hash come from the DATABASE, never from here. A locally
   * invented hash would be a number the server has never seen, and an approval
   * recorded against it would prove nothing at all.
   */
  const issue = useCallback((version: number, hash: string) => {
    setIssued({ at: new Date().toISOString(), total: totals.grandTotal, version, hash });
  }, [totals.grandTotal]);

  const revise = useCallback(() => {
    setIssued(null);
    setAnswer(null);
  }, []);

  /**
   * Everything the estimator has changed by hand, in one object.
   *
   * The shell watches this and saves it when it settles. Derived lines are
   * deliberately absent: they are recomputed from proposals, catalog and rates
   * on load, so storing them would only let them go stale.
   */
  const draft = useMemo<JobDraft>(() => ({
    prices, manualHours, quantities, refinishHours, dismissed, extra,
    removedLines, lineEdits, operations,
  }), [prices, manualHours, quantities, refinishHours, dismissed, extra,
       removedLines, lineEdits, operations]);

  return {
    draft,
    list, shots, gaps, capture, photos,
    proposals, verdicts, decide, rate,
    prompts: built.prompts, lines, totals,
    setPrice: (key: string, price: number) => setPrices((p) => ({ ...p, [key]: price })),
    /**
     * Overrule the model on repair versus replace.
     *
     * Draft state, never written back over the proposal: the model's own answer
     * stays on record so the acceptance rate keeps measuring what the model
     * actually said, not what the shop wished it had said.
     */
    setOperation: (key: string, operation: string) => {
      setOperations((p) => ({ ...p, [key]: operation }));
      input.onOperationOverride?.(key, operation);
    },
    clearOperation: (key: string) =>
      setOperations((p) => { const { [key]: _drop, ...rest } = p; return rest; }),
    operations,
    /** Strike a line off this estimate. The proposal record is untouched. */
    removeLine: (lineId: string) =>
      setRemovedLines((p) => (p.includes(lineId) ? p : [...p, lineId])),
    restoreLine: (lineId: string) =>
      setRemovedLines((p) => p.filter((id) => id !== lineId)),
    removedLines,
    editLine: (lineId: string,
               patch: { description?: string; partType?: string; partNumber?: string }) =>
      setLineEdits((p) => ({ ...p, [lineId]: { ...p[lineId], ...patch } })),
    setQuantity: (lineId: string, quantity: number) =>
      setQuantities((p) => ({ ...p, [lineId]: quantity })),
    setRefinish: (key: string, hours: number) =>
      setRefinishHours((p) => ({ ...p, [key]: hours })),
    /** Refinish hours attached to a line, by the line's own id. */
    refinishFor: (lineId: string) => {
      const key = lineId.includes('#')
        ? lineId.slice(0, lineId.lastIndexOf('#')) : lineId;
      return refinishHours[key] ?? 0;
    },
    setHours: (lineId: string, hours: number) =>
      setManualHours((p) => ({ ...p, [lineId]: hours })),
    dismissPrompt: (key: string) => setDismissed((d) => [...d, key]),
    addLine: (line: EstimateLine) => setExtra((e) => [...e, line]),
    issued, issue, revise,
    answer, setAnswer,
  };
}

export type Job = ReturnType<typeof useJob>;
